"use client";

import { ArrowRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Portfolio() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const grid = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const industries = [{
    id: 1,
    name: "Industrial Manufacturing",
    sector_type: "Heavy Industry",
    hazard_classification: "High Risk",
    description: "Comprehensive fire suppression and risk engineering for large-scale production lines and assembly plants."
  }, {
    id: 2,
    name: "Aerospace & Defense",
    sector_type: "Technology",
    hazard_classification: "Critical Risk",
    description: "Specialized protection for high-value composite fabrication, hangars, and sensitive testing facilities."
  }, {
    id: 3,
    name: "Pharmaceuticals",
    sector_type: "Life Sciences",
    hazard_classification: "Chemical Hazard",
    description: "Clean-room compatible systems designed for volatile organic compound storage and laboratory safety."
  }, {
    id: 4,
    name: "Petrochemical & Energy",
    sector_type: "Energy",
    hazard_classification: "Explosive Risk",
    description: "Robust mitigation strategies for hydrocarbon processing, refineries, and large-scale fuel storage."
  }, {
    id: 5,
    name: "Data Centers",
    sector_type: "Infrastructure",
    hazard_classification: "Operational Risk",
    description: "Gaseous suppression systems that protect critical digital infrastructure without water-based damage."
  }, {
    id: 6,
    name: "Semiconductor Fab",
    sector_type: "Technology",
    hazard_classification: "Toxic Materials",
    description: "Advanced detection and ultra-pure water systems for pyrophoric gas handling and clean-room environments."
  }, {
    id: 7,
    name: "Automotive Production",
    sector_type: "Industrial",
    hazard_classification: "Operational Risk",
    description: "Protection for automated robotic welding lines, paint booths, and emerging EV battery storage."
  }, {
    id: 8,
    name: "Power Generation",
    sector_type: "Energy",
    hazard_classification: "High Voltage",
    description: "Fire safety engineering for turbine halls, transformers, and renewable energy storage facilities."
  }, {
    id: 9,
    name: "Logistics & Warehousing",
    sector_type: "Supply Chain",
    hazard_classification: "High-Pile Storage",
    description: "ESFR sprinkler systems optimized for high-density automated storage and retrieval systems (ASRS)."
  }, {
    id: 10,
    name: "Chemical Processing",
    sector_type: "Industrial",
    hazard_classification: "Reactive Hazard",
    description: "Advanced containment and suppression for exothermic reaction vessels and hazardous material storage."
  }];
  const industryLinks: Record<string, string> = {
    "Industrial Manufacturing": "/Industries/industrial-manufacturing",
    "Aerospace & Defense": "/Industries/aerospace-defense",
    "Pharmaceuticals": "https://bedrocksafetyengineering.com/industries/pharmaceutical-fire-protection/",
    "Petrochemical & Energy": "/Industries/petrochemical-energy",
    "Data Centers": "/Industries/data-centers",
    "Semiconductor Fab": "/Industries/semiconductor-fab",
    "Automotive Production": "https://bedrocksafetyengineering.com/industries/automotive-fire-protection/",
    "Power Generation": "/Industries/power-generation",
    "Logistics & Warehousing": "/Industries/logistics-warehousing",
    "Chemical Processing": "/Industries/chemical-processing"
  };
  return <section data-section-id="11337" id="industry-portfolio" className="bg-background text-foreground py-28 md:py-36">
      <div className="mx-auto max-w-[1280px] px-6 md:px-8 lg:px-12">
        <div ref={header.ref} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 transition-all duration-700 ease-out" style={{
        opacity: header.isVisible ? 1 : 0,
        transform: header.isVisible ? "translateY(0)" : "translateY(12px)"
      }}>
          <div className="lg:col-span-5">
            <div className="font-default text-[13px] font-medium uppercase" style={{
            letterSpacing: "0.14em",
            color: "var(--muted-foreground)"
          }}>
              Industries We Serve
            </div>
            <div className="mt-6 h-px w-16 bg-border" />
            <h2 className="font-default font-bold mt-8 text-foreground" style={{
            fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
            letterSpacing: "-0.015em",
            lineHeight: 1.1
          }}>
              10 Industries Reliant on{" "}
              <span className="italic" style={{
              color: "var(--primary)"
            }}>
                Hazardous Production Systems
              </span>
            </h2>
          </div>

          <div className="lg:col-span-7 lg:pt-2">
            <p className="font-default text-foreground/90" style={{
            fontSize: "clamp(1.0625rem, 1.15vw, 1.1875rem)",
            lineHeight: 1.55,
            letterSpacing: "-0.005em",
            maxWidth: "56ch"
          }}>
              Our engineering expertise spans the most demanding industrial sectors. We provide 
              specialized fire protection and risk management solutions tailored to the unique 
              hazard profiles of high-stakes environments, ensuring operational continuity and 
              regulatory compliance across global supply chains. We serve any industry requiring 
              advanced fire protection engineering.
            </p>
          </div>
        </div>

        <div ref={grid.ref} className="mt-20 md:mt-24 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {industries.map((industry, i) => {
          return <div key={industry.id} data-index={i} className="transition-all ease-out" style={{
            opacity: grid.isVisible ? 1 : 0,
            transform: grid.isVisible ? "translateY(0)" : "translateY(12px)",
            transitionDuration: "600ms",
            transitionDelay: `${i * 50}ms`
          }}>
                <Card className="group relative bg-card border border-border rounded-lg p-8 md:p-10 h-full gap-0 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-primary hover:shadow-lg">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] font-bold tracking-widest text-muted-foreground uppercase">
                      Sector: {industry.sector_type}
                    </span>
                    <div className="px-2 py-0.5 bg-muted rounded-lg text-[10px] font-bold text-primary uppercase tracking-wider">
                      {industry.hazard_classification}
                    </div>
                  </div>

                  <h3 className="font-default mt-8 text-card-foreground" style={{
                fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
                fontWeight: 600,
                letterSpacing: "-0.005em",
                lineHeight: 1.25
              }}>
                    {industry.name}
                  </h3>

                  <p className="font-default mt-4 text-card-foreground/80 flex-grow" style={{
                fontSize: "clamp(0.9375rem, 1vw, 1rem)",
                lineHeight: 1.6
              }}>
                    {industry.description}
                  </p>

                  <div className="mt-8 pt-6 border-t border-border">
                    <Link to={industryLinks[industry.name] || "/IndustriesPage"} className="inline-flex items-center gap-2 font-default text-card-foreground transition-colors duration-200 ease-out hover:text-primary" style={{
                  fontSize: "0.9375rem",
                  fontWeight: 500,
                  textDecoration: "underline",
                  textUnderlineOffset: "4px",
                  textDecorationThickness: "1px"
                }}>
                      View more about this industry
                      <ArrowRight className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5" aria-hidden="true" />
                    </Link>
                  </div>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}